/**
 * @file ProductAttributesEditor.tsx
 * @description Редактор характеристик товара «название — значение»
 * (phase19.16 Р2а, SB33, решение пользователя 2026-10-12). До 30 строк,
 * порядок перетаскиванием (мышь, палец, клавиатура), «+ Добавить
 * характеристику», удаление. Пустые строки не сохраняются; замечания по
 * строке — когда фокус ушёл из неё. Контракт `value/onChange` — как у
 * остальных групп карточки товара.
 */

import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "../../../../components/ui/button";
import { DND_ACCESSIBILITY_RU } from "../../../../lib/site-builder/dnd-ru";
import {
  ATTRIBUTES_MAX,
  attributeKeyInputId,
  attributeRowErrors,
  moveAttributeRow,
  newAttributeRow,
  type ProductAttributeRow,
} from "../product-attributes";
import { ProductAttributeRowItem } from "./ProductAttributeRowItem";

interface Props {
  value: ProductAttributeRow[];
  onChange: (rows: ProductAttributeRow[]) => void;
  disabled?: boolean;
}

export function ProductAttributesEditor({ value, onChange, disabled }: Props) {
  // Строки, из которых уходил фокус: замечания показываем только у них.
  const [left, setLeft] = useState<ReadonlySet<string>>(() => new Set());
  // Новая строка — фокус в её «Название» после отрисовки.
  const focusId = useRef<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const errors = attributeRowErrors(value);
  const full = value.length >= ATTRIBUTES_MAX;

  useEffect(() => {
    if (!focusId.current) return;
    document.getElementById(attributeKeyInputId(focusId.current))?.focus();
    focusId.current = null;
  }, [value]);

  function onDragEnd(event: DragEndEvent) {
    const from = value.findIndex((r) => r.id === String(event.active.id));
    const to = event.over ? value.findIndex((r) => r.id === String(event.over?.id)) : -1;
    if (from < 0 || to < 0 || from === to) return;
    onChange(moveAttributeRow(value, from, to));
  }

  function add() {
    if (full) return;
    const row = newAttributeRow();
    focusId.current = row.id;
    onChange([...value, row]);
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <span className="text-sm font-medium text-on-surface">Характеристики</span>
        <span className="type-caption tabular-nums text-on-surface-variant">
          {value.length} из {ATTRIBUTES_MAX}
        </span>
      </div>
      <p className="type-caption text-on-surface-variant">
        Покажутся на странице товара таблицей, если в шаблоне есть элемент «Характеристики».
      </p>

      {value.length > 0 ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd} accessibility={DND_ACCESSIBILITY_RU}>
          <SortableContext items={value.map((r) => r.id)} strategy={verticalListSortingStrategy}>
            <ul className="flex flex-col gap-1" aria-label="Характеристики товара">
              {value.map((row, index) => (
                <ProductAttributeRowItem
                  key={row.id}
                  row={row}
                  index={index}
                  error={left.has(row.id) ? (errors[index] ?? null) : null}
                  disabled={disabled}
                  onChange={(next) => onChange(value.map((r) => (r.id === row.id ? next : r)))}
                  onRemove={() => onChange(value.filter((r) => r.id !== row.id))}
                  onLeave={() => setLeft((s) => (s.has(row.id) ? s : new Set(s).add(row.id)))}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      ) : null}

      {full ? (
        <p className="type-caption text-on-surface-variant">Добавлено максимум — {ATTRIBUTES_MAX} характеристик.</p>
      ) : (
        <Button type="button" variant="outline" className="self-start max-lg:h-12" onClick={add} disabled={disabled}>
          <Plus aria-hidden />
          Добавить характеристику
        </Button>
      )}
    </div>
  );
}
